import { Injectable, inject } from '@angular/core';
import {filter, Observable, ReplaySubject, Subscription, timer} from 'rxjs';
import {map, take} from 'rxjs/operators';
import {LoggerService} from './logger.service';
import {ZendeskMetadata} from '../components/zendesk-chat/zendesk-metadata';
import {environment} from '../../environments/environment';
import {Event, ActivatedRouteSnapshot, NavigationEnd, ActivatedRoute} from '@angular/router';
import {Location} from '@angular/common';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare let zE: any; // Declare zE to satisfy the compiler (outside library)

@Injectable({
	providedIn: 'root'
})
export class ZendeskService {
	private activatedRoute = inject(ActivatedRoute);
	private location = inject(Location);
	private logger = inject(LoggerService);

	// keeps track of most recent visited page's tags & URL
	metadata = new ReplaySubject<ZendeskMetadata>(1);

	// timer used to reset the widget
	resetTimer$: Subscription | undefined;

	isProduction = environment.production;

	load(): Promise<void> {
		const src = `https://static.zdassets.com/ekr/snippet.js?key=${environment.zendeskKey}`;
		return new Promise((resolve) => {
			const script = document.createElement('script');
			script.id = 'ze-snippet';
			script.type = 'text/javascript';
			script.src = src;
			script.onload = () => {
				resolve();
			};
			document.head.appendChild(script);
		});
	}

	// subscribe to ActivatedRoute event to set Zendesk tags for metadata
	subscribeToRouteChanges(events: Observable<Event>): Subscription {
		return events.pipe(
			// Navigation event tells us when we are done (some navigations involve multiple outlets)
			filter(event => event instanceof NavigationEnd),
			map(() => this.routeInfoToMetadata(this.activatedRoute.snapshot)),
		).subscribe(metadata => {
			//console.log('Navigation ends; metadata: ', metadata);
			this.setMetaData(metadata);
		});
	}

	routeInfoToMetadata(snapshot: ActivatedRouteSnapshot): ZendeskMetadata {
		let tags: string[] = [];

		// Process each child activated routes from the root and concat tags
		tags = tags.concat(this.generateMetadataTagsForChildren(snapshot.root));
		tags.push('FIT');

		// For non-Production environments add a TEST tag
		if (!this.isProduction) {
			tags.push('Test Test Test Test');
		}

		// note: truncate array to avoid 'The maximum amounts of tags that you can set is 20' zendesk error
		tags = tags.slice(0, 20);

		const currentPageUrl = `${environment.base}${this.location.path(true)}`;
		return new ZendeskMetadata(tags, currentPageUrl);
	}

	private generateMetadataTagsForChildren(snapshot: ActivatedRouteSnapshot) {
		let tags: Array<string> = [];

		// For each child route, add tags
		snapshot.children.forEach(child => {
			// Convert URL segments to tags
			let routeTags = child.url.map(segment => segment.path);
			routeTags = routeTags.flatMap(tag => tag.split(',')); // split any segments that are comma-delimited (e.g., :mcags in comparisons)
			routeTags = routeTags.filter(tag => tag.length > 0); // filter out any empty strings

			// 1st Priority - Resolved route data (one “code” tag & one human readable tag)
			// Handle government type resolved data
			const governmentType = child.data['governmentType'];
			this.pushTag(tags, governmentType?.descriptionPlural);
			this.pushTag(tags, governmentType?.code, 'govTypeCode');

			// Handle local government resolved data
			const localGovernment = child.data['localGovernment'];
			this.pushTag(tags, localGovernment?.fullNameForSearching);
			this.pushTag(tags, localGovernment?.mcag, 'mcag');

			// Handle local governments (with invalid governments) resolved data
			const localGovernments = child.data['localGovernments'];
			if (localGovernments instanceof Array) {
				localGovernments.forEach(govt => {
					this.pushTag(tags, govt?.fullNameForSearching);
					this.pushTag(tags, govt?.mcag, 'mcag');
					this.pushTag(tags, govt?.id, 'invalidMcag'); // For InvalidGovernment
				})
			}

			// Handle logical account resolved data
			const logicalAccount = child.data['logicalAccount'];
			this.pushTag(tags, logicalAccount?.name);
			this.pushTag(tags, logicalAccount?.logicalAccount, 'logicalAccount');

			// Handle scrollToMcag route parameter
			const scrollToMcag = child.params['scrollToMcag'];
			this.pushTag(tags, scrollToMcag, 'scrollToMcag');

			// 2nd Priority - Route segments (one tag per segment in reverse order)
			tags = tags.concat(routeTags.reverse());

			// Concat tags from its children
			if (child.children.length > 0) {
				tags = tags.concat(this.generateMetadataTagsForChildren(child));
			}
		});

		return tags;
	}

	// Add values to the tag array if they are defined
	private pushTag(array: Array<string>, value: string | undefined, label?: string) {
		if (value === undefined) {
			return;
		}

		// Use a regular expression to match any character that is NOT
		// alphanumeric (a-z, A-Z, 0-9), dash (-), underscore (_), colon (:), or forward slash (/)
		// The 'g' flag ensures all occurrences are replaced, not just the first.
		const cleanValue = value.replace(/[^a-zA-Z0-9\-_:/]/g, ' ');

		array.push(label ? `${label}:${cleanValue}` : cleanValue);
	}

	onWidgetOpen = () => {
		//console.log(`You have opened the messaging Web Widget, zenDeskTags: `, this.zendeskService.metadata);
		// kill reset timer if set
		this.resetTimer$?.unsubscribe();
	}

	private setMetaData(metadata: ZendeskMetadata) {
		// console.debug('setting metadata tags: ', metadata.tags);
		// set Tags
		zE("messenger:set", "conversationTags", metadata.tags);

		// set Current Page URL
		//console.debug('setting metadata url: ', metadata.currentPageUrl);
		zE("messenger:set", "conversationFields", [
			{id: environment.zeCurrentPageUrlFieldId, value: metadata.currentPageUrl},
		]);
	}

	onWidgetClose = () => {
		//console.log(`You have closed the messaging Web Widget.`);
		if (environment.zeResetOnCloseTime !== 0) { // If set to zero, we don't perform reset
			// Create reset callback function; after reset, we want to set up the next conversation with the current page's metadata
			const resetCallback = () => {
				//console.log(`The messaging Web Widget has been reset after ${environment.zeResetOnCloseTime}ms.`)
				this.metadata.pipe(take(1)).subscribe(metadata => this.setMetaData(metadata));
			}
			this.resetTimer$ = timer(environment.zeResetOnCloseTime)
				.subscribe(() => zE("messenger", "resetWidget", resetCallback));
		}
	}

}
